import {
  reconcileBiometricUser,
  clearPersistedNativeSession,
  persistNativePrimarySession,
} from "@features/BiometricAuthentication/biometricUnlock";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { Platform } from "react-native";

import { useRequestSessionRefresh } from "@/navigators/SessionChecker/SessionRefreshContext";
import { buildStaffAppUrl } from "@/utils/getStaffAppUrlString";
import { useFlags } from "@/utils/launchDarkly";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  clearNativeSessionSignedOut,
  resumeNativeSessionPersistence,
} from "@whoosh/relay/src";

import { useMutationWithError } from "../../../../../../utils/hooks/useMutationWithError";
import { currentProfileAtom } from "../../../../../Club/atoms";
import { sessionAtom } from "../../../../../Session";
import { isUserAuthenticated_atom } from "../../../../atoms";
import {
  ProceedToAccountCreationStepAtom,
  proceedToJoinNewClubStepAtom,
} from "../atoms/actions";
import { verificationCodeStepDataAtom } from "../atoms/selectors";
import { VerificationModalCompleteActionAtom } from "../atoms/stores";
import {
  shouldRetryAccountCreationAfterVerification,
  useTryToCreateAccount,
} from "../components/VerifiedAuthenticationModalBody/components/AccountCreationStep/hooks/useTryToCreateAccount";
import { CreateCustomerUserInput } from "./__generated__/useCreateNewCustomerUserMutation.graphql";
import { useVerifyPhoneCodeMutation } from "./__generated__/useVerifyPhoneCodeMutation.graphql";

export const useVerifyPhoneCode = (clubSlug?: string) => {
  const { biometricAuth } = useFlags();
  const [verifyPhoneCode, isVerifying] =
    useMutationWithError<useVerifyPhoneCodeMutation>(
      userCheckPhoneMutationString
    );

  const setSession = useSetAtom(sessionAtom);
  const setCurrentProfile = useSetAtom(currentProfileAtom);
  const setIsUserAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const requestSessionRefresh = useRequestSessionRefresh();
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const { showToast } = useToast();
  const proceedToAccountCreation = useSetAtom(ProceedToAccountCreationStepAtom);
  const proceedToJoinClub = useSetAtom(proceedToJoinNewClubStepAtom);
  const completeAction = useAtomValue(VerificationModalCompleteActionAtom);
  const verificationStepState = useAtomValue(verificationCodeStepDataAtom);
  const [tryToCreateAccount, isCreating] = useTryToCreateAccount();

  const handlePhoneCodeVerification = ({
    phoneNumber,
    code,
    onError,
  }: {
    phoneNumber: string;
    code: string;
    onError: () => void;
  }) => {
    const startVerification = () => {
      if (Platform.OS !== "web") {
        resumeNativeSessionPersistence();
      }

      verifyPhoneCode({
        variables: { input: { phoneNumber, code } },
        onCompleted: async (res) => {
          if (
            res.responsePayload?.success &&
            res.responsePayload.session?.user &&
            Platform.OS !== "web"
          ) {
            const authenticatedUserId = res.responsePayload?.session?.user?.id;
            if (authenticatedUserId && biometricAuth === true) {
              await reconcileBiometricUser(authenticatedUserId);
            }
            await persistNativePrimarySession();
            await clearNativeSessionSignedOut();
          }

          const user = res.responsePayload?.session?.user;

          /**
           * Are we creating a new account?
           */

          const formDataToSubmit = verificationStepState?.formDataToSubmit;
          const hasAssignmentTokenAndFormDataToSubmit =
            shouldRetryAccountCreationAfterVerification({
              hasAssignmentToken: !!res.responsePayload?.assignmentToken,
              hasAuthenticatedUser: !!user,
              hasFormDataToSubmit: !!formDataToSubmit,
              verificationType: verificationStepState?.verificationType,
            });

          if (
            hasAssignmentTokenAndFormDataToSubmit &&
            res.responsePayload?.assignmentToken &&
            formDataToSubmit
          ) {
            const currentFormData: CreateCustomerUserInput = {
              ...formDataToSubmit,
              phoneAssignmentToken: res.responsePayload.assignmentToken,
            };
            tryToCreateAccount(currentFormData);
          } else {
            const hasAssignmentTokenButNoUser =
              !user &&
              res.responsePayload?.success &&
              res.responsePayload.assignmentToken;

            const hasExistingMemberUser =
              res.responsePayload?.session?.user?.__typename === "MemberUser";

            if (hasAssignmentTokenButNoUser) {
              //Assume we verified so lets go on to the next step.
              proceedToAccountCreation({
                verifiedPhone: phoneNumber,
                verifiedEmail: null,
                phoneAssignmentToken: res.responsePayload.assignmentToken,
                emailAssignmentToken: null,
              });
              //If we have a user, we just logged in. Hooray
            } else if (hasExistingMemberUser) {
              const currentProfile =
                res.responsePayload?.session?.currentClientProfile;

              if (user) {
                setIsUserAuthenticated(true);
                setSession({
                  user: {
                    id: user.id,
                    email: user.email!,
                    username: user.username!,
                    clubs:
                      user.memberProfiles?.edges?.map((edge) => ({
                        id: edge?.node?.member?.club?.id || "",
                        slug: edge?.node?.member?.club?.slug || "",
                        name: edge?.node?.member?.club?.name || "",
                        logoImage:
                          edge?.node?.member?.club?.logoImage?.publicId || "",
                      })) || [],
                  },
                });
                if (
                  currentProfile?.member?.__typename === "Member" ||
                  currentProfile?.member?.__typename === "Customer"
                ) {
                  const ghinNumber =
                    currentProfile?.member?.departmentMemberProfiles?.find(
                      (dmp) => !!dmp?.ghinNumber
                    )?.ghinNumber;
                  setCurrentProfile({
                    memberId: currentProfile?.member?.id || "",
                    membershipTypeId:
                      currentProfile?.member?.membershipType?.id ?? "",
                    firstName: currentProfile?.member?.firstName || "",
                    lastName: currentProfile?.member?.lastName || "",
                    memberGroups: [],
                    hasSeenNotificationNudgeModal:
                      user?.metadata?.hasSeenNotificationNudgeModal || false,
                    email: currentProfile?.member?.email || "",
                    phoneNumber:
                      currentProfile?.member?.phoneNumber?.nationalNumber || "",
                    ghinNumber,
                  });
                } else {
                  setCurrentProfile(null);
                }
                const clientType =
                  res.responsePayload?.session?.currentClientProfile?.member
                    ?.__typename;
                const needsMemberProfile = clientType === "AnonymousCustomer";
                if (needsMemberProfile) {
                  proceedToJoinClub();
                } else {
                  const firstProfile =
                    res.responsePayload?.session?.user?.memberProfiles
                      ?.edges?.[0]?.node;
                  const userFirstClubSlug = firstProfile?.member?.club?.slug;

                  const defaultClubSlug = clubSlug
                    ? clubSlug
                    : userFirstClubSlug;

                  setIsRefreshingSession(true);
                  requestSessionRefresh({
                    onCompleted: () => {
                      setIsRefreshingSession(false);
                      completeAction?.(defaultClubSlug);
                    },
                    onError: () => {
                      setIsRefreshingSession(false);
                      onError();
                    },
                  });
                }
              }
            } else {
              /**
               * This is a staff user. On web we ship them to the staff app.
               *
               * On native we just show them a toast because we can't ship them anywhere.
               */
              setIsUserAuthenticated(false);
              if (Platform.OS === "web") {
                window.location.href = buildStaffAppUrl(window.location);
              } else {
                clearPersistedNativeSession().then(() => {
                  setIsUserAuthenticated(false);
                  showToast({
                    description: t`Whoops! For now, only members can log into the mobile application.`,
                    semanticStyle: "error",
                    toastId: "staff-login-error",
                  });
                });
              }
            }
          }
        },
        onCompletedErrorCustom(errors) {
          onError();
          return errors;
        },
      });
    };

    startVerification();
  };

  return [
    handlePhoneCodeVerification,
    isVerifying || isCreating || isRefreshingSession,
  ] as const;
};

export const userCheckPhoneMutationString = graphql`
  mutation useVerifyPhoneCodeMutation($input: VerifyPhoneCodeInput!) {
    responsePayload: verifyPhoneCode(input: $input) {
      success
      assignmentToken
      session {
        ...useSyncAnalyticsTrackingPermission_session
        currentClientProfile {
          ... on MemberProfile {
            id
            active
          }
          member {
            ... on Client {
              ... on AnonymousCustomer {
                __typename
              }
              ... on Customer {
                __typename
                id
                firstName
                lastName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
                membershipType {
                  id
                }
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    ghinNumber
                    department {
                      slug
                    }
                  }
                }
              }
              ... on Member {
                departmentMemberProfiles(type: GOLF) {
                  ... on GolfMemberProfile {
                    ghinNumber
                    department {
                      slug
                    }
                  }
                }
                __typename
                id
                firstName
                lastName
                email
                phoneNumber {
                  nationalNumber
                }
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
                membershipType {
                  id
                }
                memberGroups {
                  id
                }
              }
            }
          }
        }
        masquerader {
          email
          username
        }
        user {
          __typename
          id
          email
          username
          ... on MemberUser {
            memberProfiles(active: true) {
              edges {
                node {
                  member {
                    club {
                      id
                      name
                      slug
                      logoImage {
                        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                        publicId: renderingKey
                      }
                    }
                  }
                }
              }
            }
            metadata {
              hasSeenNotificationNudgeModal
            }
          }
        }
      }
      errors {
        field
        code
        message
      }
    }
  }
`;
